/**
 * A PDF plan set as one flat field of sheets.
 *
 * Incumbent viewers show one sheet at a time because a whole set at readable
 * DPI is gigabytes of bitmap. This keeps the flat layout and pays for it with
 * two levels of detail:
 *
 * - every sheet is rasterized once at `OVERVIEW_PX`, and that image is what the
 *   sheet's shape and the thumbnail rail show. It is the whole set, always
 *   resident, and it is what you read when you are looking at more than a sheet
 *   at a time;
 * - whatever the viewport actually covers is redrawn at the resolution it is
 *   being viewed at, as one canvas per sheet the viewport touches, laid over
 *   the top.
 *
 * The second tier is the one that makes zoom unbounded. A whole-sheet render is
 * not: fixing a sheet at N pixels fixes the zoom past which it is a blur, and
 * paying for more pixels costs the square. A tile costs the viewport no matter
 * how far in you are, so the only thing that grows with zoom is how little of
 * the sheet you can see.
 *
 * Canvas units are PDF points, which puts zoom 1 at the sheet's natural size
 * and makes every scale here a pixels-per-point number.
 */
import { debug } from "debug";
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import type { BoxModel } from "tldraw";
import { invariant } from "ts-invariant";

import { getPdfjsLib, loadPdfDocument } from "./pdfjs";

const log = debug("studio:pdf-plan-set");

/** Longest side of the always-resident render of every sheet. */
const OVERVIEW_PX = 1000;

/** Gutter between sheets: 4 inches in points. */
const SHEET_GAP = 4 * 72;

/** Below this, a sheet is a scan with a few stray runs, not a text layer. */
const SEARCHABLE_MIN_CHARS = 200;

export interface PlanSheet {
  pageNumber: number;
  /** The sheet's bookmark title, or "Page N". */
  label: string;
  /** Placement in page space, in PDF points. */
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SheetRender {
  /** A PNG data URL: tldraw's asset validator rejects `blob:`. */
  url: string;
}

/** One sheet's share of a viewport redraw, placed in field coordinates. */
export interface SheetTile {
  sheetIndex: number;
  x: number;
  y: number;
  w: number;
  h: number;
  canvas: HTMLCanvasElement;
}

/** A hit from {@link PlanSet.search}, placed in field coordinates. */
export interface SheetMatch {
  sheetIndex: number;
  text: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PlanSet {
  sheets: PlanSheet[];
  bounds: BoxModel;
  /** The always-resident image of a whole sheet. Idempotent per sheet. */
  renderOverview(index: number): Promise<SheetRender>;
  /**
   * `region` of the field redrawn at `scale` device pixels per point, one
   * canvas per sheet it touches. Aborting cancels the render in progress.
   */
  renderTiles(
    region: BoxModel,
    scale: number,
    signal: AbortSignal,
  ): Promise<SheetTile[]>;
  search(query: string): Promise<SheetMatch[]>;
  /** Sheets with no text layer, which no query can ever hit. */
  unsearchableSheets(): Promise<number[]>;
  dispose(): void;
}

export async function openPlanSet(src: string | ArrayBuffer): Promise<PlanSet> {
  const started = performance.now();
  const data = typeof src === "string" ? await fetchBytes(src) : src;
  // pdfjs transfers the buffer to its worker and detaches it, so hand it a copy
  // and keep the cached original openable again.
  const loadingTask = await loadPdfDocument({ data: data.slice(0) });
  const pdf = await loadingTask.promise;
  const labels = await readBookmarkLabels(pdf);
  log(
    "opened %d pages in %dms",
    pdf.numPages,
    (performance.now() - started) | 0,
  );
  const pages: { page: PDFPageProxy; label: string; w: number; h: number }[] =
    [];
  for (let n = 1; n <= pdf.numPages; n++) {
    const page = await pdf.getPage(n);
    const viewport = page.getViewport({ scale: 1 });
    pages.push({
      page,
      label: labels.get(n - 1) ?? `Page ${n}`,
      w: viewport.width,
      h: viewport.height,
    });
  }

  const sheets = layOutGrid(pages);
  const overviews = new Map<number, Promise<SheetRender>>();
  let textIndex: Promise<TextRun[][]> | null = null;

  return {
    sheets,
    bounds: fieldBounds(sheets),
    renderOverview(index) {
      let pending = overviews.get(index);
      if (!pending) {
        pending = rasterize(pages[index].page, OVERVIEW_PX);
        overviews.set(index, pending);
      }
      return pending;
    },
    async renderTiles(region, scale, signal) {
      const started = performance.now();
      const tiles: SheetTile[] = [];
      for (const [index, sheet] of sheets.entries()) {
        // No tile below the resolution the overview already has: it would be
        // the same picture at the same density, drawn again.
        if (scale <= OVERVIEW_PX / Math.max(sheet.w, sheet.h)) continue;
        const clip = intersection(region, sheet);
        if (!clip) continue;
        signal.throwIfAborted();
        tiles.push({
          sheetIndex: index,
          ...clip,
          canvas: await drawClip(pages[index].page, sheet, clip, scale, signal),
        });
      }
      log(
        "%d tiles at %s px/pt in %dms",
        tiles.length,
        scale.toFixed(2),
        (performance.now() - started) | 0,
      );
      return tiles;
    },
    async search(query) {
      const needle = query.trim().toLowerCase();
      if (!needle) return [];
      const index = (textIndex ??= buildTextIndex(pages));
      return (await index).flatMap((runs, sheetIndex) =>
        runs
          .filter((run) => run.lower.includes(needle))
          .map(({ text, x, y, w, h }) => ({
            sheetIndex,
            text,
            x: x + sheets[sheetIndex].x,
            y: y + sheets[sheetIndex].y,
            w,
            h,
          })),
      );
    },
    async unsearchableSheets() {
      const index = await (textIndex ??= buildTextIndex(pages));
      return index.flatMap((runs, i) => {
        const chars = runs.reduce((n, run) => n + run.text.length, 0);
        return chars < SEARCHABLE_MIN_CHARS ? [i] : [];
      });
    },
    dispose() {
      overviews.clear();
      loadingTask.destroy();
    },
  };
}

/**
 * Downloads still in flight, so a remount joins one rather than starting a
 * second. Entries are dropped once they settle: an upload overwrites the same
 * S3 key, so the URL is not a content version and holding bytes against it
 * would serve a replaced plan set (and would pin a failure forever).
 */
const inFlight = new Map<string, Promise<ArrayBuffer>>();

/** pdfjs would read a 403's HTML body and call it a corrupt PDF. */
function fetchBytes(src: string): Promise<ArrayBuffer> {
  const pending =
    inFlight.get(src) ??
    fetch(src)
      .then((res) => {
        invariant(res.ok, `Could not fetch the PDF (HTTP ${res.status})`);
        return res.arrayBuffer();
      })
      .finally(() => inFlight.delete(src));

  inFlight.set(src, pending);
  return pending;
}

/** Reading order, each sheet centred in a cell sized to the largest sheet. */
function layOutGrid(
  pages: readonly { label: string; w: number; h: number }[],
): PlanSheet[] {
  const cols = Math.ceil(Math.sqrt(pages.length));
  const cellW = Math.max(...pages.map((p) => p.w));
  const cellH = Math.max(...pages.map((p) => p.h));

  return pages.map((page, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    return {
      pageNumber: i + 1,
      label: page.label,
      x: col * (cellW + SHEET_GAP) + (cellW - page.w) / 2,
      y: row * (cellH + SHEET_GAP) + (cellH - page.h) / 2,
      w: page.w,
      h: page.h,
    };
  });
}

function fieldBounds(sheets: readonly PlanSheet[]): BoxModel {
  const minX = Math.min(...sheets.map((s) => s.x));
  const minY = Math.min(...sheets.map((s) => s.y));
  const maxX = Math.max(...sheets.map((s) => s.x + s.w));
  const maxY = Math.max(...sheets.map((s) => s.y + s.h));
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** The part of `sheet` that `region` covers, in field coordinates. */
function intersection(region: BoxModel, sheet: PlanSheet): BoxModel | null {
  const x = Math.max(region.x, sheet.x);
  const y = Math.max(region.y, sheet.y);
  const w = Math.min(region.x + region.w, sheet.x + sheet.w) - x;
  const h = Math.min(region.y + region.h, sheet.y + sheet.h) - y;
  return w > 0 && h > 0 ? { x, y, w, h } : null;
}

/**
 * One sheet's clip, drawn at `scale`.
 *
 * The canvas is only as big as the clip, and `transform` slides the page so the
 * clip lands at its origin: the whole sheet at this scale would be past the
 * browser's canvas limit long before the zoom is.
 */
async function drawClip(
  page: PDFPageProxy,
  sheet: PlanSheet,
  clip: BoxModel,
  scale: number,
  signal: AbortSignal,
): Promise<HTMLCanvasElement> {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(clip.w * scale));
  canvas.height = Math.max(1, Math.round(clip.h * scale));

  const task = page.render({
    canvas,
    canvasContext: canvas.getContext("2d")!,
    viewport: page.getViewport({ scale }),
    transform: [
      1,
      0,
      0,
      1,
      -(clip.x - sheet.x) * scale,
      -(clip.y - sheet.y) * scale,
    ],
  });
  const stop = () => task.cancel();
  signal.addEventListener("abort", stop);
  try {
    await task.promise;
  } finally {
    signal.removeEventListener("abort", stop);
  }
  return canvas;
}

async function rasterize(
  page: PDFPageProxy,
  maxPx: number,
): Promise<SheetRender> {
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({
    scale: maxPx / Math.max(base.width, base.height),
  });

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext("2d")!;
  const started = performance.now();
  await page.render({ canvas, canvasContext: ctx, viewport }).promise;

  // PNG, measured against the sheets in `examples/plans`: it encodes as fast as
  // JPEG (~55ms either way) and is lossless, which matters when the content is
  // 1px linework and 8pt notes that JPEG rings around. WebP is a quarter of the
  // size but takes ~200ms per megapixel to encode, so it only pays off once
  // encoding is off the main thread.
  const url = canvas.toDataURL("image/png");
  log(
    "page %d @%dpx in %dms",
    page.pageNumber,
    maxPx,
    (performance.now() - started) | 0,
  );

  // Drop the ~45MB bitmap now rather than waiting for GC.
  canvas.width = 0;
  canvas.height = 0;

  return { url };
}

/** One run of text from the PDF, boxed in sheet-local points. */
interface TextRun {
  text: string;
  lower: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The searchable text of every page, in the same coordinate space the sheets
 * are laid out in.
 *
 * Only sheets authored as vectors have one: across `examples/plans` the split
 * was 19/19, 4/15 and 3/24 pages. A scanned sheet yields nothing here and is
 * simply unfindable until there's OCR, which `unsearchableSheets` reports so
 * the UI can say so rather than implying the set has been searched.
 */
async function buildTextIndex(
  pages: readonly { page: PDFPageProxy }[],
): Promise<TextRun[][]> {
  const started = performance.now();
  // Through the loader, not a static import: pdfjs has to stay out of the
  // workspace bundle and behind the polyfill that has to precede it.
  const { Util } = await getPdfjsLib();
  const index: TextRun[][] = [];

  for (const { page } of pages) {
    const viewport = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    const runs: TextRun[] = [];

    for (const item of content.items) {
      if (!("str" in item)) continue;
      const text = item.str.trim();
      if (!text) continue;

      // The item's matrix is in unrotated PDF space; composing it with the
      // viewport's puts it where the sheet was actually rasterized.
      const m = Util.transform(viewport.transform, item.transform);
      const h = Math.hypot(m[1], m[3]);
      runs.push({
        text,
        lower: text.toLowerCase(),
        x: m[4],
        y: m[5] - h,
        w: item.width,
        h,
      });
    }
    index.push(runs);
  }

  log("indexed text in %dms", (performance.now() - started) | 0);
  return index;
}

/**
 * Sheet names off the PDF's bookmarks, keyed by page index.
 *
 * Scraping the title block instead was tried and dropped: on the sets in
 * `examples/plans` it labelled 3 of 58 pages, two of them wrong. Scanned title
 * blocks want OCR, not a corner heuristic.
 */
async function readBookmarkLabels(
  pdf: PDFDocumentProxy,
): Promise<Map<number, string>> {
  const labels = new Map<number, string>();

  const walk = async (nodes: readonly RawOutlineNode[]) => {
    for (const node of nodes) {
      const dest =
        typeof node.dest === "string"
          ? await pdf.getDestination(node.dest)
          : node.dest;
      const target = Array.isArray(dest) ? dest[0] : null;
      if (target) {
        // Real sets bookmark pages that later re-assembly dropped.
        const index = await pdf.getPageIndex(target).catch(() => -1);
        const title = node.title.trim();
        if (index >= 0 && title && !labels.has(index)) {
          labels.set(index, title);
        }
      }
      if (node.items?.length) await walk(node.items);
    }
  };

  await walk((await pdf.getOutline()) ?? []);
  return labels;
}

/** pdfjs types `dest` as `unknown`; `getPageIndex` takes the ref inside it. */
interface RawOutlineNode {
  title: string;
  dest: string | unknown[] | null;
  items?: RawOutlineNode[];
}
